import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { User, Palette, Layers } from "lucide-react";

export const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <section
      id="about"
      ref={ref}
      className="py-24 sm:py-32 px-6 sm:px-12 lg:px-24"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="mb-16">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D4FF00] mb-4">
              About Me
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
              Crafting Digital Experiences
            </h2>
          </motion.div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 sm:gap-8">
            {/* Main About Card */}
            <motion.div
              variants={itemVariants}
              className="md:col-span-8 bg-[#0C0C0C] border border-white/10 p-8 sm:p-12 flex flex-col relative overflow-hidden group hover:border-white/30 transition-all duration-500 card-glow"
            >
              <User className="text-[#D4FF00] mb-6" size={32} />
              <h3 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white mb-4">
                Who I Am
              </h3>
              <p className="text-[#888888] leading-relaxed mb-4">
                I am a passionate Mobile App Developer with strong expertise in
                Android (Java & Kotlin) and modern app development solutions. I
                specialize in building scalable applications such as fintech
                apps, translation apps, dating platforms, and utility tools.
              </p>
              <p className="text-[#888888] leading-relaxed">
                Along with development, I focus on clean UI/UX design using
                Figma to deliver user-friendly and engaging mobile experiences.
                My goal is to create apps that not only function flawlessly but
                also delight users with their intuitive design.
              </p>
            </motion.div>

            {/* Image Card */}
            <motion.div
              variants={itemVariants}
              className="md:col-span-4 bg-[#0C0C0C] border border-white/10 relative overflow-hidden group hover:border-white/30 transition-all duration-500 min-h-[300px]"
            >
              <img
                src="https://images.unsplash.com/photo-1542866203-71304d7c6303?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxmZW1hbGUlMjBzb2Z0d2FyZSUyMGRldmVsb3BlciUyMHBvcnRyYWl0JTIwZGFya3xlbnwwfHx8fDE3NzU4OTg4MDd8MA&ixlib=rb-4.1.0&q=85"
                alt="Vaidehi Virani"
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
            </motion.div>

            {/* UI/UX Design Card */}
            <motion.div
              variants={itemVariants}
              className="md:col-span-6 bg-[#0C0C0C] border border-white/10 p-8 flex flex-col relative overflow-hidden group hover:border-white/30 transition-all duration-500 card-glow"
            >
              <Palette className="text-[#D4FF00] mb-4" size={28} />
              <h3 className="text-xl font-semibold tracking-tight text-white mb-3">
                UI/UX Design
              </h3>
              <p className="text-[#888888] leading-relaxed text-sm">
                Creating intuitive and visually stunning interfaces using Figma
                that prioritize user experience and engagement.
              </p>
            </motion.div>

            {/* Development Card */}
            <motion.div
              variants={itemVariants}
              className="md:col-span-6 bg-[#0C0C0C] border border-white/10 p-8 flex flex-col relative overflow-hidden group hover:border-white/30 transition-all duration-500 card-glow"
            >
              <Layers className="text-[#D4FF00] mb-4" size={28} />
              <h3 className="text-xl font-semibold tracking-tight text-white mb-3">
                Full-Stack Mobile
              </h3>
              <p className="text-[#888888] leading-relaxed text-sm">
                End-to-end mobile development from concept to deployment,
                ensuring seamless performance across Android and iOS platforms.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
